import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  Button,
  Divider,
  Skeleton,
  alpha,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LinkIcon from "@mui/icons-material/Link";
import HistoryIcon from "@mui/icons-material/History";

import {
  findStatus,
  findPriority,
  findCategory,
} from "../constants/taskConstants";

function InfoRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ color: "text.secondary", display: "flex", pt: 0.25 }}>{icon}</Box>
      <Box sx={{ minWidth: 110 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-word" }}>
          {value || "—"}
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * Drawer lateral con el detalle completo de una tarea.
 *
 * Hace fetch de GET /tasks/{id} al abrirse para asegurar datos frescos
 * (incluye locationName y lastUpdatedBy que getTasks no devuelve).
 */
export default function TaskDetailDrawer({
  open,
  taskId,
  onClose,
  getTaskById,
  currentEmployeeId,
  onEdit,
  onComplete,
  onCancel,
  onDelete,
}) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !taskId) {
      setTask(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getTaskById(taskId)
      .then((data) => { if (!cancelled) setTask(data); })
      .catch(() => { if (!cancelled) setTask(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, taskId, getTaskById]);

  const statusCfg   = task ? findStatus(task.status)     : null;
  const priorityCfg = task ? findPriority(task.priority) : null;
  const categoryCfg = task ? findCategory(task.category) : null;

  const isDone   = task?.status === "completed" || task?.status === "cancelled";
  const isCreator = task && String(task.creatorId) === String(currentEmployeeId);
  const isAssignee = task && String(task.assigneeId) === String(currentEmployeeId);
  const canEdit = !isDone && (isCreator || isAssignee);
  const canDelete = isCreator;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
          color: "white",
          px: 3,
          py: 2.5,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600 }}>
              Task #{task?.id || taskId}
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5, lineHeight: 1.3, wordBreak: "break-word" }}>
              {loading ? <Skeleton sx={{ bgcolor: alpha("#fff", 0.3) }} /> : task?.title || "—"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {task && !loading && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            {statusCfg && (
              <Chip
                label={statusCfg.label}
                size="small"
                sx={{ bgcolor: alpha("#fff", 0.95), color: statusCfg.color, fontWeight: 700 }}
              />
            )}
            {priorityCfg && (
              <Chip
                label={`${priorityCfg.label} priority`}
                size="small"
                sx={{ bgcolor: alpha("#fff", 0.95), color: priorityCfg.color, fontWeight: 700 }}
              />
            )}
          </Stack>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ p: 3, flex: 1, overflowY: "auto" }}>
        {loading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={60} />
            <Skeleton variant="rounded" height={140} />
          </Stack>
        ) : !task ? (
          <Typography color="text.secondary">Task not found.</Typography>
        ) : (
          <Stack spacing={2.5}>
            {task.description && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                  {task.description}
                </Typography>
              </Box>
            )}

            <Divider />

            <Stack spacing={1.5}>
              <InfoRow
                icon={<EventIcon fontSize="small" />}
                label="Due Date"
                value={task.dueDate || "Not set"}
              />
              {categoryCfg && (
                <InfoRow
                  icon={<LinkIcon fontSize="small" />}
                  label="Category"
                  value={categoryCfg.label}
                />
              )}
              {task.relatedType && (
                <InfoRow
                  icon={<LinkIcon fontSize="small" />}
                  label="Related"
                  value={task.relatedType}
                />
              )}
              <InfoRow
                icon={<AssignmentIndIcon fontSize="small" />}
                label="Assignee"
                value={task.assigneeName || "Unassigned"}
              />
              <InfoRow
                icon={<PersonIcon fontSize="small" />}
                label="Created by"
                value={task.creatorName || task.createdBy}
              />
              {(task.patientName || task.cipId) && (
                <InfoRow
                  icon={<LocalHospitalIcon fontSize="small" />}
                  label="Patient"
                  value={[task.patientName, task.cipId && `CIP ${task.cipId}`].filter(Boolean).join(" — ")}
                />
              )}
              {(task.locationName || task.locationId) && (
                <InfoRow
                  icon={<LocationOnIcon fontSize="small" />}
                  label="Location"
                  value={task.locationName || `Location ${task.locationId}`}
                />
              )}
            </Stack>

            <Divider />

            <Stack spacing={1.5}>
              <InfoRow
                icon={<HistoryIcon fontSize="small" />}
                label="Created"
                value={task.createdAt ? new Date(task.createdAt).toLocaleString() : "—"}
              />
              {task.updatedAt && (
                <InfoRow
                  icon={<HistoryIcon fontSize="small" />}
                  label="Last update"
                  value={new Date(task.updatedAt).toLocaleString()}
                />
              )}
              {task.completedAt && (
                <InfoRow
                  icon={<CheckCircleOutlineIcon fontSize="small" />}
                  label="Completed"
                  value={new Date(task.completedAt).toLocaleString()}
                />
              )}
            </Stack>
          </Stack>
        )}
      </Box>

      {/* Footer actions */}
      {task && !loading && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.5),
          }}
        >
          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" sx={{ rowGap: 1 }}>
            <Tooltip title={canDelete ? "" : "Only the creator can delete"}>
              <span>
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={!canDelete}
                  onClick={() => onDelete?.(task.id)}
                  sx={{ textTransform: "none" }}
                >
                  Delete
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="text"
              size="small"
              startIcon={<CancelIcon />}
              disabled={isDone}
              onClick={() => onCancel?.(task.id)}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Tooltip title={canEdit ? "" : "Only creator or assignee can edit"}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  disabled={!canEdit}
                  onClick={() => onEdit?.(task)}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Edit
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<CheckCircleOutlineIcon />}
              disabled={isDone}
              onClick={() => onComplete?.(task.id)}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Complete
            </Button>
          </Stack>
        </Box>
      )}
    </Drawer>
  );
}
